import { Button, ButtonGroup, NoContentIndicator, Table } from '@ver-uds/react';
import { Alert, Icon } from '@ver-uds/uswds-react';
import React, { ReactNode, useState } from 'react';
import type { Column } from 'react-table';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import EVerifyUserModal, { ModalSubmitData } from './EVerifyUserModal';
import EVerifyUser from './data/EVerifyUser';
import SubmitAction from '../SubmitAction';
import { TableData } from './data/EVerifyUsersTableData';
import AlertConstants from '../../../constants/AlertConstants';

export interface EVerifyUsersFormProps {
  addButtonText: string;
  noRowsText: string;
  usersData: EVerifyUser[];
  columns: Column<TableData>[];
  tableText?: string;
  modalHeaderText?: string;
  onNext: () => void;
  onSubmit: (action: SubmitAction, user: EVerifyUser) => void;
  isAddDisabled?: (userData: EVerifyUser[]) => boolean;
  checkUsersForError?: (users: EVerifyUser[]) => AlertConstants | undefined;
}

const EVerifyUsersForm = ({
  addButtonText,
  noRowsText,
  usersData,
  columns,
  tableText,
  modalHeaderText,
  onNext,
  onSubmit,
  isAddDisabled,
  checkUsersForError,
}: EVerifyUsersFormProps): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const headerText = `Open modal to add ${modalHeaderText || 'E-Verify User'}`;

  const formatPhoneNumber = (rawPhoneNumber: string): string => {
    // Case: phone number (10-digits)
    if (rawPhoneNumber.length === 10) {
      return rawPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2 - $3');
    }
    // Case: phone number + extension
    return rawPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})(\d{1,4})/, `($1) $2 - $3 ext $4`);
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeUser, setActiveUser] = useState<EVerifyUser | undefined>(undefined);

  const generateRowActions = (user: EVerifyUser): ReactNode => {
    const editLabel = `Open modal to edit user ${user.firstName} ${user.lastName}`;
    const deleteLabel = `Delete user ${user.firstName} ${user.lastName}`;
    return (
      <ButtonGroup>
        <Button
          key={editLabel}
          type="button"
          variant="tertiary"
          onClick={(): void => {
            setActiveUser(user);
            setModalIsOpen(true);
          }}
          aria-label={editLabel}
        >
          <Icon name="edit" />
        </Button>
        <Button
          key={deleteLabel}
          type="button"
          variant="tertiary"
          onClick={(): void => {
            onSubmit(SubmitAction.DELETE, user);
          }}
          aria-label={deleteLabel}
        >
          <Icon name="delete" />
        </Button>
      </ButtonGroup>
    );
  };

  const hasProgramAdminColumn = columns.some(({ accessor }) => accessor === 'admin');
  const hasMouSignatoryColumn = columns.some(({ accessor }) => accessor === 'mouSignatory');

  const generateTableData = (data: EVerifyUser[]): TableData[] => {
    const transformedData = data.map((user) => {
      return {
        id: user.id,
        lastName: user.lastName,
        firstName: user.firstName,
        middleInitial: user.middleInitial || '---',
        phoneNumber: formatPhoneNumber(`${user.phoneNumber}${user.phoneNumberExtension || ''}`),
        emailAddress: user.emailAddress,
        mouSignatory: user.mouSignatory,
        admin: user.admin,
        actions: generateRowActions(user),
      };
    });
    return transformedData;
  };

  const onModalSubmit = (data: ModalSubmitData) => {
    if (data.action === SubmitAction.ADD) {
      onSubmit(SubmitAction.ADD, data.user);
    } else {
      onSubmit(SubmitAction.UPDATE, data.user);
    }
    setModalIsOpen(false);
    setErrorMessage(undefined);
    setActiveUser(undefined);
  };

  const handleOnNext = () => {
    if (checkUsersForError) {
      const error = checkUsersForError(usersData);
      if (error) {
        setErrorMessage(error);
        return;
      }
    }

    setErrorMessage(undefined);
    onNext();
  };

  return (
    <>
      <section>
        <div className="display-flex flex-wrap grid-col-12 margin-y-2">
          <div className="grid-col-12 desktop:grid-col-9">
            {tableText ? <p>{tableText}</p> : <></>}
            {errorMessage && (
              <Alert status="error" slim>
                <Alert.Text>{errorMessage}</Alert.Text>
              </Alert>
            )}
          </div>
          <div className="display-flex grid-col-12 desktop:grid-col-3 flex-justify-end flex-align-self-end">
            <Button
              className="margin-top-2 tablet:margin-top-2 desktop:margin-top-0"
              data-testid="add-site-button"
              aria-label={headerText}
              onClick={(): void => setModalIsOpen(true)}
              size="small"
              disabled={isAddDisabled ? isAddDisabled(usersData) : false}
              iconLeft={<Icon name="add" />}
            >
              {addButtonText}
            </Button>
          </div>
        </div>
        <EVerifyUserModal
          user={activeUser}
          addButtonText={addButtonText}
          foundMouSignatoryId={usersData.find((user) => user.mouSignatory === 'Yes')?.id}
          showUserRoleOptions={hasProgramAdminColumn && hasMouSignatoryColumn}
          modalHeaderText={modalHeaderText}
          open={modalIsOpen}
          close={() => {
            setModalIsOpen(false);
            setActiveUser(undefined);
          }}
          onSubmit={onModalSubmit}
        />
        {usersData.length > 0 ? (
          <Table
            columns={columns}
            data={generateTableData(usersData || [])}
            id="everify-users-table"
            variant="default"
          />
        ) : (
          <NoContentIndicator height={100} id="noContentIndicator">
            <p>{noRowsText}</p>
          </NoContentIndicator>
        )}
      </section>
      <EnrollmentStepperButtons nextButtonType="submit" onNext={handleOnNext} />
    </>
  );
};

export default EVerifyUsersForm;
